// Global navbar search — queries the main entity tables in parallel and
// returns a categorised result set the dropdown can render directly.
//
// Categories: people, tickets, channels, messages, events, documents, notes, kb.
// Each category caps at 5 hits.
//
// migrated 2026-04-27 to SDK route workspaceProject.search (rpc).
//
// The server wraps `workspace_global_search(q, surfaces, limit)` — a single
// SQL function the wrapper exposes via /search. The function is expected
// to return rows with at least { surface, id, title, subtitle, href }
// fields. While migrations land, surfaces lacking server-side support are
// quietly elided. People are still resolved client-side from `root.people`.
//
// TODO[server]: wire the workspace_global_search() SQL function so all
// surfaces fan out server-side; once it ships, surface-specific queries
// here are no longer needed.

import { getSDK } from '../../../shared/functions/sdk.js'

const _LIMIT = 5
const _DEBOUNCE_MS = 200

// Category metadata for display ordering and labels
const CATEGORY_META = {
  people: { label: 'People', icon: 'people', order: 0 },
  tickets: { label: 'Tickets', icon: 'ticket', order: 1 },
  channels: { label: 'Channels', icon: 'chat', order: 2 },
  messages: { label: 'Messages', icon: 'chat', order: 3 },
  events: { label: 'Events', icon: 'calendar', order: 4 },
  documents: { label: 'Documents', icon: 'document', order: 5 },
  notes: { label: 'Notes', icon: 'document', order: 6 },
  kb: { label: 'Knowledge Base', icon: 'bookmark', order: 7 }
}

// Map a server-side row {surface, id, title, ...} to the client-side
// flat result shape used by the dropdown. Surfaces without an explicit
// mapping fall through to a generic shape — let the server label them.
const _SURFACE_HREF = {
  tickets: (r) => `/tickets/my?ticket=${r.number || r.id}`,
  notes: (r) => `/documents/notes?id=${r.id}`,
  kb: (r) => `/documents/knowledge-base?id=${r.id}`,
  events: (r) => `/calendar?event=${r.id}`,
  channels: (r) => `/chat?channel=${r.id}`,
  messages: (r) => `/chat?channel=${r.channel_id}&message=${r.id}`,
  documents: (r) => `/documents?id=${r.id}`
}

const _shapeRow = (row) => {
  const surface = row.surface || row._category
  const id = row.id || row._id
  const title = row.title || row._title || `#${row.number || id}`
  const subtitle = row.subtitle || row._subtitle || row.author || ''
  const hrefBuilder = _SURFACE_HREF[surface]
  return {
    _category: surface,
    _id: id,
    _title: title,
    _subtitle: subtitle,
    _href: row.href || row._href || (hrefBuilder ? hrefBuilder(row) : '#')
  }
}

// Group flat results by category, respecting display order
const _groupResults = (results) => {
  const grouped = {}
  for (const r of results) {
    if (!grouped[r._category]) grouped[r._category] = []
    grouped[r._category].push(r)
  }
  return Object.entries(grouped)
    .sort(([a], [b]) => (CATEGORY_META[a]?.order ?? 99) - (CATEGORY_META[b]?.order ?? 99))
    .map(([cat, items]) => ({
      category: cat,
      label: CATEGORY_META[cat]?.label || cat,
      icon: CATEGORY_META[cat]?.icon || 'search',
      items
    }))
}

export const globalSearch = async function (query) {
  const root = this.state.root
  const q = (query || '').trim()
  root.update({ navSearchQuery: query || '', _navSearchLoading: !!q, _navSearchOpen: !!q })
  if (!q) {
    root.update({ navSearchResults: [], _navSearchGrouped: [], _navSearchLoading: false, _navSearchOpen: false })
    return
  }
  await new Promise(r => setTimeout(r, _DEBOUNCE_MS))
  if (root.navSearchQuery !== query) return
  let serverResults = []
  try {
    const result = await getSDK().execute('workspaceProject.search', 'rpc', {
      q,
      options: { limit: _LIMIT }
    })
    if (root.navSearchQuery !== query) return
    const rows = Array.isArray(result?.data) ? result.data : (Array.isArray(result) ? result : [])
    serverResults = rows.map(_shapeRow)
  } catch (err) {
    // Swallow until the server-side search RPC ships. People-only fallback
    // still renders so the dropdown never goes empty.
    console.warn('[globalSearch] server search unavailable, returning people-only', err?.message || err)
  }

  if (root.navSearchQuery !== query) return
  const ql = q.toLowerCase()
  const peopleHits = (root.people || [])
    .filter(p => (p.name || '').toLowerCase().includes(ql) || (p.email || '').toLowerCase().includes(ql))
    .slice(0, _LIMIT)
    .map(p => ({
      _category: 'people',
      _id: p.email,
      _title: p.name || p.email,
      _subtitle: p.email,
      _href: `/people?u=${encodeURIComponent(p.email)}`
    }))
  const all = [...peopleHits, ...serverResults]
  root.update({
    navSearchResults: all,
    _navSearchGrouped: _groupResults(all),
    _navSearchLoading: false,
    _navSearchOpen: true
  })
}

export const closeNavSearch = function () {
  this.state.root.update({
    navSearchQuery: '',
    navSearchResults: [],
    _navSearchGrouped: [],
    _navSearchLoading: false,
    _navSearchOpen: false
  })
}

export const navigateSearchResult = function (href) {
  const root = this.state.root
  root.update({
    navSearchQuery: '',
    navSearchResults: [],
    _navSearchGrouped: [],
    _navSearchLoading: false,
    _navSearchOpen: false
  })
  const rootEl = this.getRoot()
  this.context?.utils?.router(href, rootEl, {}, { pushState: true, scrollToTop: true })
}

export { CATEGORY_META }
